import { Form } from '@form/Form';
import FormInput from '@form/FormInput';
import { faCheck, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../hooks/useApi';
import { useClubSlug } from '../Clubs/hooks';
import { Column, DataTable, DataTableContainer, TableActionButton } from '../DataTable';
import { AsyncListRef } from '../DataTable/DataTable';
import { TextTemplate } from './types';

interface Props {
    clubSlug?: string;
    currentText?: string;
    onSelectTemplate?: (text: string) => void;
}

const TextTemplateManager = ({ clubSlug: _clubSlug, currentText, onSelectTemplate }: Props) => {
    const { t } = useTranslation(['form', 'common']);
    const api = useApi();
    const clubSlug = useClubSlug(_clubSlug);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [editMode, setEditMode] = useState<TextTemplate>();
    const asyncList = useRef<AsyncListRef>(null);

    const handleSaveNewTemplate = useCallback(
        (data: FieldValues) => {
            setIsLoading(true);
            return api({
                url: `club/${clubSlug}/text-templates`,
                method: 'POST',
                data: { ...data, text: currentText },
                onBefore: () => setIsLoading(false),
                onSuccess: () => {
                    asyncList.current?.reload();
                }
            });
        },
        [clubSlug, currentText]
    );

    const handleEditTemplate = useCallback(
        (data: FieldValues) => {
            if (!editMode) return;

            setIsLoading(true);
            return api({
                url: `text-templates/${editMode.id}`,
                method: 'PUT',
                data,
                onBefore: () => setIsLoading(false),
                onSuccess: ({ data }) => {
                    setEditMode(undefined);
                    asyncList.current?.update(data.id, data);
                }
            });
        },
        [editMode]
    );

    const handleSelectTemplate = useCallback(
        (template: TextTemplate) => {
            setIsLoading(true);
            void api({
                url: `text-templates/${template.id}`,
                method: 'GET',
                onBefore: () => setIsLoading(false),
                onSuccess: ({ data }) => {
                    onSelectTemplate?.(data.text);
                }
            });
        },
        [onSelectTemplate]
    );

    const handleDeleteTemplate = useCallback((template: TextTemplate) => {
        setIsLoading(true);
        void api({
            url: `text-templates/${template.id}`,
            method: 'DELETE',
            onBefore: () => setIsLoading(false),
            onSuccess: () => {
                asyncList.current?.remove(template.id);
            }
        });
    }, []);

    const columns = useMemo(
        (): Column[] => [
            { uid: 'title', name: t('textTemplates.columns.title') },
            {
                uid: 'text',
                name: t('textTemplates.columns.text'),
                renderer: (textTemplateText: TextTemplate['text']) => (
                    <span className="tw-text-md tw-inline-block tw-overflow-hidden tw-overflow-ellipsis tw-leading-3">
                        {textTemplateText.replace(/<\/?[^>]+(>|$)/g, ' ')}
                    </span>
                )
            },
            {
                uid: 'actions',
                align: 'end',
                name: t('textTemplates.columns.actions')
            }
        ],
        [t]
    );

    if (editMode)
        return (
            <div className="tw-p-1">
                <Button block size="xs" onPress={() => setEditMode(undefined)}>
                    {t('textTemplates.back')}
                </Button>
                <Form disableErrorWarning onSubmit={handleEditTemplate} defaultValues={editMode}>
                    <FormInput.WYSIWYG hideTemplates name="text" />
                    <FormInput.Submit disableErrorWarning className="!tw-mt-1" isLoading={isLoading}>
                        Speichern
                    </FormInput.Submit>
                </Form>
            </div>
        );

    return (
        <div className="tw-p-1">
            <div className="tw-my-2 tw-flex tw-flex-col tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-pb-2">
                <DataTableContainer className="tw-min-h-[30vh]">
                    <DataTable
                        asyncListRef={asyncList}
                        fetchUrl={`club/${clubSlug}/text-templates`}
                        columns={columns}
                        tableProps={{
                            sticked: true,
                            bordered: true,
                            borderWeight: 'light',
                            compact: true
                        }}
                        rowActions={(textTemplate) => (
                            <TableActionButton.Container>
                                <TableActionButton
                                    icon={faCheck}
                                    tooltip={t('textTemplates.actions.select')}
                                    onPress={() => handleSelectTemplate(textTemplate)}
                                />
                                <TableActionButton
                                    icon={faEdit}
                                    tooltip={t('actions.edit')}
                                    color="warning"
                                    onPress={() => setEditMode(textTemplate)}
                                />
                                <TableActionButton
                                    icon={faTrash}
                                    tooltip={t('actions.delete')}
                                    color="error"
                                    onPress={() => handleDeleteTemplate(textTemplate)}
                                />
                            </TableActionButton.Container>
                        )}
                    />
                </DataTableContainer>
            </div>
            <Form disableErrorWarning onSubmit={handleSaveNewTemplate}>
                <div className="tw-grid tw-grid-cols-4 tw-items-start tw-gap-4">
                    <div className="tw-col-span-3">
                        <FormInput
                            placeholder={t('textTemplates.titlePlaceholder')}
                            name="title"
                            withoutShifting
                            helperText={
                                !currentText
                                    ? t('textTemplates.emptyTextWarning')
                                    : null
                            }
                        />
                    </div>
                    <FormInput.Submit disableErrorWarning isLoading={isLoading} disabled={!currentText}>
                        Speichern
                    </FormInput.Submit>
                </div>
            </Form>
        </div>
    );
};

export default TextTemplateManager;
